import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TabPages.css';
import '../Calendar/Calendar.css';
import { useCurrentUser } from '../../../../contexts/CurrentUserContext';
import { useCalDateUpdates } from '../../../../hooks/dataUpdates';
import type { DropdownMenuItem } from '../../../../components/DropdownMenu/DropdownMenu';
import type { ElementSceneInfo } from '../../../../types/element.types';
import type { SlotInfo, ScheduleInfo, CalDateInfo } from '../../../../types/schedule.types';
import { ClipboardPanel } from './ClipboardPanel/ClipboardPanel';
import type { ScheduleDetailsTabType } from './tabPages.types';
import CurrentDateDetails from './CurrentDateDetails';
import SceneList from './SceneList';
import { ScheduleSceneRowsProvider } from './Components/sceneCards';
import type { ScenesListTabType } from './Components/sceneCards';
import ScheduleManager from './ScheduleManager';

interface TabPagesProps {
  canEdit?: boolean;
  canCopy?: boolean;
  canManage?: boolean;
  breakdownID?: string;
  elementRelations?: ElementSceneInfo[];
  scheduleID?: string;
  slots?: SlotInfo[];
  calDates?: CalDateInfo[];
  schedule?: ScheduleInfo | null;
  currentDate?: Date | null;
  currentDateInfo?: {
    shootingDayNumber: number | null;
    totalDays: number | null;
    shift: string | undefined;
    totalPages: number;
    featuredRolesQuantity: number;
    crowdRolesQuantity: number;
    cast: string;
  } | null;
  onDateSelect?: (date: Date) => void;
  onSlotDelete?: (slotID: string) => void;
  /** slot/calDate 等变更后由父组件重新拉取日历行数据并可刷新 schedule 摘要 */
  onScheduleRowsRefresh?: () => void;
  onDateShift?: (daysOffset: number) => void;
  onSelectSchedule?: (
    scheduleID: string,
    options?: { fromScheduleList?: boolean; openScenesListTab?: boolean; openDetailsTab?: boolean },
  ) => void;
  initialActiveTab?: ScheduleDetailsTabType;
  onConfirmDeleteSchedule?: (scheduleID: string) => Promise<void>;
  /** 打开 ScheduleSearch（切换/新建同一分场下的其他拍摄计划） */
  onOpenScheduleSearch?: () => void;
  clipboardGroups?: import('../../Hooks/useScheduleClipboard').ClipboardGroup[];
  getSceneInfo?: (sceneID: string | undefined) => import('../../../../types/scene.types').SceneInfo | undefined;
  /** 日历上拖拽 slot/整日时，为非剪贴 tab 透出剪贴板等覆盖 UI */
  calendarDnDActive?: boolean;
  onClipboardRemoveGroup?: (groupIndex: number) => void;
  /** 将剪贴组恢复到日历当前选中日期（与整组拖入日历等价） */
  onClipboardAddGroupToCurrentDate?: (groupIndex: number) => void | Promise<void>;
  onDetailsTabChange?: (tab: ScheduleDetailsTabType) => void;
  normalizeDatesIfNeeded?: (dateStrs: string[]) => Promise<void>;
}

const TabPages: React.FC<TabPagesProps> = ({
  breakdownID,
  elementRelations = [],
  scheduleID,
  slots = [],
  calDates = [],
  schedule,
  currentDate: currentDateProp,
  currentDateInfo,
  onDateSelect,
  onSlotDelete,
  onScheduleRowsRefresh,
  onDateShift,
  onSelectSchedule,
  initialActiveTab,
  onConfirmDeleteSchedule,
  onOpenScheduleSearch,
  clipboardGroups = [],
  getSceneInfo,
  calendarDnDActive = false,
  onClipboardRemoveGroup,
  onClipboardAddGroupToCurrentDate,
  onDetailsTabChange,
  normalizeDatesIfNeeded,
  canEdit = false,
  canCopy = false,
  canManage = false,
}) => {
  const { t } = useTranslation();
  const { setLocalOnlyField, localOnlyData } = useCurrentUser();
  const { upsertCalDateData } = useCalDateUpdates();
  const [activeTab, setActiveTab] = useState<ScheduleDetailsTabType>(initialActiveTab || 'currentDate');

  useEffect(() => {
    if (initialActiveTab) {
      setActiveTab(initialActiveTab);
    }
  }, [initialActiveTab]);

  const currentDate = useMemo(() => currentDateProp || new Date(), [currentDateProp]);

  const [scenesListTab, setScenesListTab] = useState<ScenesListTabType>('all');
  const [sortKey, setSortKey] = useState<'sceneCode' | 'sceneSet' | 'sceneLocation' | 'scenePeriod'>('sceneCode');
  const userChoseScenesListTabRef = useRef(false);

  useEffect(() => {
    setScenesListTab('all');
    setSortKey('sceneCode');
    userChoseScenesListTabRef.current = false;
  }, [scheduleID]);

  const { effectiveScenesListTab, effectiveSortKey } = useMemo(() => {
    const def: ScenesListTabType = 'all';
    const defSort: 'sceneCode' | 'sceneSet' | 'sceneLocation' | 'scenePeriod' = 'sceneCode';
    if (userChoseScenesListTabRef.current) {
      return { effectiveScenesListTab: scenesListTab, effectiveSortKey: sortKey };
    }
    let fromCache: { tab: ScenesListTabType; sort: typeof defSort } | null = null;
    const sceneListTabs = localOnlyData.userLastUsedScheduleScenesListTab;
    if (activeTab === 'scenes' && scheduleID && Array.isArray(sceneListTabs) && sceneListTabs.length > 0) {
      const tabCache = sceneListTabs.find((item) => {
        const parts = item.split(';');
        return parts.length >= 2 && parts[0] === scheduleID;
      });
      if (tabCache) {
        const [, tabAndSort] = tabCache.split(';');
        const [tabIdentifier, sortValue] = (tabAndSort || ',').split(',');
        const tab =
          tabIdentifier === 'all' || tabIdentifier === 'unscheduled' || tabIdentifier === 'scheduled'
            ? (tabIdentifier as ScenesListTabType)
            : def;
        const sk = ['sceneCode', 'sceneSet', 'sceneLocation', 'scenePeriod'].includes(sortValue)
          ? (sortValue as typeof defSort)
          : defSort;
        fromCache = { tab, sort: sk };
      }
    }
    const tab = fromCache?.tab ?? scenesListTab;
    const sk = fromCache?.sort ?? sortKey;
    return { effectiveScenesListTab: tab, effectiveSortKey: sk };
  }, [activeTab, scheduleID, scenesListTab, sortKey, localOnlyData.userLastUsedScheduleScenesListTab]);

  const sortOptions = useMemo(
    () =>
      [
        { id: 'sceneCode' as const, label: t('schedule.sortBySceneCode') },
        { id: 'sceneSet' as const, label: t('scene.set') },
        { id: 'sceneLocation' as const, label: t('schedule.sceneListSortLocation') },
        { id: 'scenePeriod' as const, label: t('schedule.sortByShootingPeriod') },
      ],
    [t],
  );

  const handleSortFieldSelect = (item: DropdownMenuItem) => {
    userChoseScenesListTabRef.current = true;
    const newSortKey = String(item.id) as 'sceneCode' | 'sceneSet' | 'sceneLocation' | 'scenePeriod';
    setSortKey(newSortKey);
    if (scheduleID) {
      setLocalOnlyField('userLastUsedScheduleScenesListTab', `${scheduleID};${effectiveScenesListTab},${newSortKey}`);
    }
  };

  const handleScenesListTabChange = (tab: ScenesListTabType) => {
    userChoseScenesListTabRef.current = true;
    setScenesListTab(tab);
    if (scheduleID) {
      setLocalOnlyField('userLastUsedScheduleScenesListTab', `${scheduleID};${tab},${effectiveSortKey}`);
    }
  };

  const getSortFieldLabel = () => {
    const option = sortOptions.find((opt) => opt.id === effectiveSortKey);
    return option?.label || t('schedule.sortBySceneCode');
  };

  const tabs: { id: ScheduleDetailsTabType; label: string }[] = [
    { id: 'currentDate', label: t('schedule.tabToday') },
    { id: 'scenes', label: t('schedule.tabBreakdown') },
    { id: 'clipboard', label: t('schedule.tabClipboard') },
    { id: 'details', label: t('schedule.tabDetails') },
  ];

  const clipboardSlotCount = useMemo(
    () =>
      clipboardGroups.reduce(
        (sum, g) => sum + g.items.reduce((s, it) => s + (it.slots?.length ?? 0), 0),
        0,
      ),
    [clipboardGroups],
  );

  const clipboardHasContent = useMemo(
    () => clipboardGroups.some((g) => g.items.length > 0),
    [clipboardGroups],
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'currentDate':
        return (
          <CurrentDateDetails
            currentDate={currentDate}
            onDateSelect={onDateSelect}
            calDates={calDates}
            scheduleID={scheduleID}
            upsertCalDate={upsertCalDateData}
            onScheduleRowsRefresh={onScheduleRowsRefresh}
            slots={slots}
            getSceneInfo={getSceneInfo}
          />
        );
      case 'scenes':
        return (
          <SceneList
            effectiveScenesListTab={effectiveScenesListTab}
            onScenesListTabChange={handleScenesListTabChange}
            sortOptions={sortOptions}
            effectiveSortKey={effectiveSortKey}
            getSortFieldLabel={getSortFieldLabel}
            onSortFieldSelect={handleSortFieldSelect}
          />
        );
      case 'clipboard':
        return (
          <ClipboardPanel
            scheduleID={scheduleID}
            groups={clipboardGroups}
            getSceneInfo={getSceneInfo ?? (() => undefined)}
            isActiveTab={true}
            onRemoveGroup={onClipboardRemoveGroup}
            onAddGroupToCurrentDate={onClipboardAddGroupToCurrentDate}
          />
        );
      case 'details':
        return (
          <ScheduleManager
            canEdit={canEdit}
            canCopy={canCopy}
            canManage={canManage}
            schedule={schedule}
            slots={slots}
            calDates={calDates}
            scheduleID={scheduleID}
            breakdownID={breakdownID}
            currentDateInfo={currentDateInfo}
            onScheduleRowsRefresh={onScheduleRowsRefresh}
            onDateShift={onDateShift}
            onSelectSchedule={onSelectSchedule}
            onOpenScheduleSearch={onOpenScheduleSearch}
            onConfirmDeleteSchedule={onConfirmDeleteSchedule}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="scheduleDetails-container">
      <div className="scheduleDetails-header-tabs">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`scheduleDetails-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              if (scheduleID) {
                setLocalOnlyField('userLastUsedScheduleTab', `${scheduleID};${tab.id}`);
              }
              onDetailsTabChange?.(tab.id);
            }}
          >
            <div className="scheduleDetails-tab-left" />
            <div className="scheduleDetails-tab-mid">
              <span className="scheduleDetails-tab-label">{tab.label}</span>
              {tab.id === 'clipboard' && clipboardHasContent ? (
                <span className="scheduleDetails-tab-clipboard-badge" title={String(clipboardSlotCount)}>
                  {clipboardSlotCount}
                </span>
              ) : null}
            </div>
            <div className="scheduleDetails-tab-right" />
          </div>
        ))}
      </div>

      <div className="scheduleDetails-tab-content scheduleDetails-tab-content-relative">
        <ScheduleSceneRowsProvider
          canEdit={canEdit}
          breakdownID={breakdownID}
          elementRelations={elementRelations}
          scheduleID={scheduleID}
          slots={slots}
          calDates={calDates}
          currentDate={currentDate}
          onSlotDelete={onSlotDelete}
          onScheduleRowsRefresh={onScheduleRowsRefresh}
          effectiveScenesListTab={effectiveScenesListTab}
          effectiveSortKey={effectiveSortKey}
          normalizeDatesIfNeeded={normalizeDatesIfNeeded}
        >
          {renderTabContent()}
        </ScheduleSceneRowsProvider>

        {activeTab !== 'clipboard' && scheduleID && calendarDnDActive && (
          <div className="clipboard-dropzone-overlay">
            <ClipboardPanel
              scheduleID={scheduleID}
              groups={clipboardGroups}
              getSceneInfo={getSceneInfo ?? (() => undefined)}
              isActiveTab={false}
              onRemoveGroup={onClipboardRemoveGroup}
              onAddGroupToCurrentDate={onClipboardAddGroupToCurrentDate}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TabPages;
